import { Variation } from '@/@core/services/app.service'
import { TableColumnsType } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack,
  Typography,
  Box,
  Chip,
  IconButton,
  Badge,
  Slide
} from '@mui/material'

import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { useContext } from 'react'
import { AbilityContext } from '@/layouts/components/acl/Can'
import { styled } from '@mui/material/styles'
import CommentOutline from 'mdi-material-ui/CommentOutline'
import { ExternalLinks } from '@/@core/services/api'
import { Circle, CircleHalfFull, MenuDown } from 'mdi-material-ui'
import {
  addCommas,
  changeV1Filter,
  chromosomeRegion,
  formatScientificToDecimal,
  formatVariantInheritance,
  isJson,
  isWindow,
  renameChildTypes,
  switchACMG,
  unique
} from '@/@core/utils/utils'
import { useRouter } from 'next/router'
import { Rating } from '@mui/material'

interface Props {
  setCurrent: any
  setOpen: any
  setHistoryOpen: any
  setCommentsOpen: any
  setTranscript_list: any
  handleMenuClick: any
  getColumnSearchProps: any
  setFilterGeneOpen: any
  setMenuType: any
  setFilterGeneData: any
  getFilterGene: any
  setIndexOpen: any
  setGeneIndexOpen: any
  setGeneOpen: any
  setAcmgOpen: any
  setOMIMOpen: any
  VariationGeneRef: any
  analysisInfo: any
  query: any
  indexChecked: boolean
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({}))

const TypographyA = styled(Typography)(() => ({
  fontSize: '0.8rem',
  color: 'inherit'
}))

const Badges = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: '-10px',
    backgroundColor: 'rgb(110,200,229)'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}))

const color = [
  { key: 'pathogenic', value: 'pathogenic', color: 'rgba(229,38,35,0.8)' },
  {
    key: 'likely pathogenic',
    value: 'likely pathogenic',
    color: 'rgba(221,109,32,0.8)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(46, 125, 50,0.8)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240,0.4)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200,0.8)'
  },
  { key: 'benign', value: 'benign', color: 'rgb(109,56,149,0.8)' },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(237, 108, 2,0.8)'
  }
]

const GLScolor = [
  { key: 'pathogenic', value: 'pathogenic', color: 'rgb(218,150,148)' },
  {
    key: 'likely pathogenic',
    value: 'likely benign',
    color: 'rgb(245,182,193)'
  },
  { key: 'drug response', value: 'drug response', color: 'rgb(186,204,147)' },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240)'
  },
  { key: 'likely benign', value: 'likely benign', color: 'rgb(107,148,200)' },
  { key: 'benign', value: 'benign', color: 'rgb(64,113,190)' },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(246,209,139)'
  }
]

export const judgeType = (str: string, arr: any[]) => {
  if (str.includes('child') && !str.includes('//')) return 'C'
  if (str.includes('child') && str.includes('//'))
    return `C${str.split('//')[1]}`
  if (str.includes('mother')) return 'M'
  if (str.includes('father')) return 'F'
  return 'O'
}

const Columns2 = React.forwardRef((props: Props, ref) => {
  const { t, i18n } = useTranslation()
  const ability = useContext(AbilityContext)
  const router = useRouter()
  const { id, ids, group } = router.query
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const type = router.asPath.includes('/samples') ? 'sample' : 'analyze'
  const isPipelineVersion = props.analysisInfo?.pipelineVersion

  // 辅助函数：获取背景色
  const getBgColor = (row: any) => {
    const classificationField = isPipelineVersion
      ? row.gls_classification
      : row.CLINSIG_final
    let bgcolor = color.find(
      (i) => i.key === classificationField?.toLowerCase()
    )
      ? (color.find((i) => i.key === classificationField?.toLowerCase())
          ?.color as any)
      : 'rgba(0, 0, 0)'

    if (
      classificationField?.toLowerCase() === 'pathogenic' ||
      classificationField?.toLowerCase() === 'likely pathogenic'
    ) {
      //
    } else if (row.loss_of_function === 1) bgcolor = '#f5d18c'

    return bgcolor
  }

  // 辅助函数：获取基因名称
  const getGeneName = (row: any) => {
    if (props.query?.title === t('MT')) {
      const genes = row.MT_genes
      return genes && Array.isArray(genes) ? genes.join(';') : row.gene
    }
    return row.gene
  }

  // 辅助函数：获取转录本ID
  const getTranscriptId = (row: any) =>
    isPipelineVersion ? row.feature : row.transcript

  // 辅助函数：获取MANE标签
  const getManeLabel = (row: any) =>
    isPipelineVersion ? row.mane ?? 'MANE' : 'MANE'

  // 辅助函数：获取rsID
  const getRsId = (row: any) => (isPipelineVersion ? row.rsid : row.rs_id)

  // 辅助函数：获取功能影响
  const getConsequence = (row: any) =>
    isPipelineVersion ? row.consequence : row.coding_effect

  // 辅助函数：获取变异位置
  const getVarLocation = (row: any) =>
    isPipelineVersion ? row.variant_locations?.join(';') : row.var_location

  // 辅助函数：获取cDNA变化
  const getHgvsc = (row: any) => (isPipelineVersion ? row.hgvsc : row.c_nomen)

  // 辅助函数：获取蛋白质变化
  const getHgvsp = (row: any) => (isPipelineVersion ? row.hgvsp : row.p_nomen)

  // 辅助函数：获取碱基变化
  const getNucChange = (row: any) =>
    isPipelineVersion ? `${row.ref}>${row.alt}` : row.nuc_change

  // 辅助函数：获取等位基因频率
  const getAlleleFrequency = (row: any) =>
    isPipelineVersion ? row.allele_balance : row.allele_frequency

  // 辅助函数：获取覆盖度
  const getCoverageValue = (row: any) =>
    isPipelineVersion ? row.ad : row.coverage

  // 辅助函数：获取质量评分
  const getQual = (row: any) => (isPipelineVersion ? row.qual : row.QUAL)

  // 辅助函数：获取SpliceAI最大评分
  const getSpliceAI = (row: any) =>
    isPipelineVersion ? row.spliceai_max_score : row.SpliceAI_max_score

  // 辅助函数：获取Revel评分
  const getRevel = (row: any) =>
    isPipelineVersion ? row.revel : row.REVEL_score

  // 辅助函数：获取AlphaM评分
  const getAlphaM = (row: any) =>
    isPipelineVersion ? row.am_pathogenicity : row.AM_pathogenicity

  // 辅助函数：获取ClinVar变异ID
  const getClinvarVariantId = (row: any) =>
    isPipelineVersion ? row.clinvar_id : row.CLINVAR_variant_id

  // 辅助函数：获取ClinVar分类
  const getClinvarClassification = (row: any) =>
    isPipelineVersion ? row.clinvar_clinsig_final : row.CLINVAR_clnsig_final

  // 辅助函数：获取ClinVar评分
  const getClinvarScore = (row: any) =>
    isPipelineVersion ? row.clinvar_gold_stars : row.CLINVAR_gold_stars

  // 辅助函数：获取Index数据
  const getIndexData = (row: any) =>
    isPipelineVersion ? row.tempGlsIndex : row.Index

  // 辅助函数：获取GeneIndex数据
  const getGeneIndexData = (row: any) =>
    isPipelineVersion ? null : row.GeneIndex

  // 辅助函数：获取ACMG评分
  const getAcmgScore = (row: any) =>
    isPipelineVersion ? row.acmg_score : row.ACMG_score

  // 辅助函数：获取ACMG分类
  const getAcmgClassification = (row: any) =>
    isPipelineVersion ? row.acmg_classification : row.ACMG_classification

  // 辅助函数：格式化ACMG分类
  const formatACMG = (classification: string) => {
    if (!classification) return ''
    return classification
      .split(' ')
      .map((word: string) => {
        if (word.includes('Uncertain')) return 'VU'
        if (word.includes('null')) return ''
        return word[0] && word[0].toUpperCase()
      })
      .join('')
  }

  // 辅助函数：判断是否显示CNV标记
  const hasCNVFlag = (row: any) => {
    const sampleList = props.analysisInfo.samples ?? []
    return (
      sampleList?.some(
        (sample: any) =>
          (sample.cnvBatchTaskId && sample.cnvBatchStatus === 'processed') ||
          sample.hasExomeDepthCnv
      ) && row.CNV
    )
  }

  // 辅助函数：处理IGV跳转
  const handleIGVClick = (e: any, row: any) => {
    if (
      (props.analysisInfo && props.analysisInfo.bamFile) ||
      props.analysisInfo.sampleType === 'fastq'
    ) {
      props.handleMenuClick(e, 'IGV')
      props.setCurrent && props.setCurrent(row)
    } else if (
      ['family_analysis', 'big_family', 'carrier'].includes(
        props.analysisInfo.analysisType
      )
    ) {
      const fqSamples = props.analysisInfo.samples?.filter(
        (item: any) => item && item.sampleType === 'fastq'
      )
      if (fqSamples && fqSamples.length) {
        props.handleMenuClick(e, 'IGV')
        props.setCurrent && props.setCurrent(row)
      } else {
        window.open(
          `${ExternalLinks.genome}?db=${
            props.analysisInfo.genomeBuild ?? 'hg19'
          }&position=chr${row.chrom}:${row.pos}-${row.pos}`
        )
      }
    } else {
      window.open(
        `${ExternalLinks.genome}?db=${
          props.analysisInfo.genomeBuild ?? 'hg19'
        }&position=chr${row.chrom}:${row.pos}-${row.pos}`
      )
    }
  }

  // 辅助函数：处理遗传模式数据
  const processInheritanceData = (item: any) => {
    const inheritances: string[] = []
    const clinicalSynopsis = isJson(item.clinicalSynopsis)
      ? JSON.parse(item.clinicalSynopsis)
      : isJson(item.clinical_synopsis)
      ? JSON.parse(item.clinical_synopsis)
      : item.clinicalSynopsis

    if (
      item &&
      clinicalSynopsis &&
      clinicalSynopsis.INHERITANCE &&
      Array.isArray(clinicalSynopsis.INHERITANCE)
    ) {
      clinicalSynopsis.INHERITANCE.forEach((str: string) =>
        inheritances.push(str.toLowerCase())
      )
    }

    return {
      ...item,
      inheritance: formatVariantInheritance(
        Array.from([...new Set(inheritances)]).join(',')
      )
    }
  }

  // 辅助函数：获取OMIM数据（合并pipelineVersion的true/false情况）
  const getOMIMData = (row: any) => {
    if (props.query && props.query.key && props.query.key === '5') {
      return { type: 'mitomap', data: row.MitoMap }
    }

    if (isPipelineVersion) {
      if (i18n?.language === 'zh' && row.omim_curate_phenotypes?.length) {
        return {
          data: unique(row.omim_curate_phenotypes, 'disease'),
          type: 'curated'
        }
      } else {
        const processedData =
          row?.omim_phenotypes?.map(processInheritanceData) ?? []
        return { data: unique(processedData, 'phenoName'), type: 'phenotypes' }
      }
    } else {
      if (i18n?.language === 'zh' && row.OMIM?.geneDiseaseTranslated?.data) {
        return {
          data: unique(row.OMIM.geneDiseaseTranslated.data, 'disease'),
          type: 'translated'
        }
      } else {
        const processedData =
          row.OMIM?.omimData?.map(processInheritanceData) ?? []
        return { data: unique(processedData, 'phenoName'), type: 'omimData' }
      }
    }
  }

  // 辅助函数：渲染OMIM项
  const renderOMIMItem = (item: any, index: number, type: string, row: any) => {
    const isCurated = type === 'curated' || type === 'translated'
    const geneticMode = isCurated ? item.genetic_mode : item.inheritance
    const diseaseName = isCurated
      ? item.disease
      : i18n?.language === 'zh'
      ? `${item.pheno_name_cn}-${item.phenoName}`
      : item.phenoName
    const keyField = isCurated ? item.disease : item.phenoMim

    return (
      <TypographyA
        sx={{
          width: '100%',
          maxWidth: 200,
          lineHeight: '24px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          cursor: 'pointer',
          color: [
            'rgb(109,56,149,0.8)',
            'rgb(107,148,200,0.8)',
            'rgba(229,38,35,0.8)'
          ].includes(getBgColor(row))
            ? 'white'
            : '#23527c',
          mb: '4px'
        }}
        onClick={() => {
          props.setCurrent(row)
          props.setOMIMOpen(true)
        }}
        key={`${keyField}_${index}`}
      >
        <Chip
          label={geneticMode || '.'}
          size="small"
          sx={{ height: 18, fontSize: '0.75rem', fontWeight: '700' }}
        />

        <span style={{ lineHeight: '24px' }}>{diseaseName}</span>
      </TypographyA>
    )
  }

  // 辅助函数：渲染Tooltip内容
  const renderTooltipContent = (OMIMValue: any[], type: string) => (
    <React.Fragment>
      {OMIMValue.map((item: any, index: any) => {
        const geneticMode =
          type === 'curated' || type === 'translated'
            ? item.genetic_mode
            : item.inheritance || ''
        const diseaseName =
          type === 'curated' || type === 'translated'
            ? item.disease
            : item.phenoName || ''
        const diseaseNameCn =
          type === 'phenotypes' || type === 'omimData' ? item.pheno_name_cn : ''

        return (
          <TypographyA
            sx={{
              fontSize: '0.85rem',
              lineHeight: '1rem',
              maxWidth: isWindow() ? '400px !important' : '200px !important'
            }}
            key={`${
              type === 'curated' || type === 'translated'
                ? item.disease
                : item.phenoName
            }_${index}`}
          >
            {`(${geneticMode || '.'})-${
              i18n?.language === 'zh' && diseaseNameCn
                ? `${diseaseNameCn}-`
                : ''
            }${diseaseName}`}
          </TypographyA>
        )
      })}
    </React.Fragment>
  )

  // 辅助函数：获取gnomAD数据
  const getGnomadData = (row: any) => {
    if (props.query?.title === t('MT')) {
      return {
        hom:
          row.AF_hom === ('.' as unknown as string)
            ? '.'
            : Number(row.AF_hom) === 0
            ? 0
            : Number(row.AF_hom)?.toFixed(8),
        het:
          row.AF_het === ('.' as unknown as string)
            ? '.'
            : Number(row.AF_het) === 0
            ? 0
            : Number(row.AF_het)?.toFixed(8)
      }
    }
    return {
      exome:
        row.gnomade_af === ('.' as unknown as string)
          ? '.'
          : row.gnomade_af === 0
          ? 0
          : formatScientificToDecimal(row.gnomade_af),
      genome:
        row.gnomadg_af === ('.' as unknown as string)
          ? '.'
          : row.gnomadg_af === 0
          ? 0
          : formatScientificToDecimal(row.gnomadg_af),
      wbbc:
        row.wbbc_af === ('.' as unknown as string)
          ? '.'
          : row.wbbc_af === 0
          ? 0
          : formatScientificToDecimal(row.wbbc_af)
    }
  }

  // 辅助函数：获取样本数据
  const getGroupData = (row: any) => {
    const groupData = isPipelineVersion
      ? row.group_data
      : row.members_data || row.samples_info
    return groupData
      ? groupData
          .map((item: any) => {
            const temp = renameChildTypes(props.analysisInfo.samples).find(
              (items: any) => items.id == item.sample_id
            )
            const sampleType = temp ? temp.type : ''
            return {
              ...item,
              type: sampleType,
              typeIndex:
                sampleType === 'affected_child'
                  ? 0
                  : sampleType === 'mother'
                  ? 2
                  : sampleType === 'father'
                  ? 1
                  : sampleType === 'child'
                  ? 0
                  : 0
            }
          })
          .sort((a: any, b: any) => a.typeIndex - b.typeIndex)
      : []
  }

  const column: TableColumnsType<Variation> = React.useMemo(() => {
    let column = [
      // 基因列
      {
        title: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Gene')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('TranscriptID')}
              </span>
            </Stack>
          </Stack>
        ),
        dataIndex: 'gene',
        key: 'gene',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const bgcolor = getBgColor(row.original)
          const badges = row.original.user_variant_comments
            ? row.original.user_variant_comments.comments.length
            : 0
          const hisBadges = row.original.tempHistory
            ? row.original.tempHistory.length
            : 0
          const transcriptId = getTranscriptId(row.original)
          const maneLabel = getManeLabel(row.original)
          const NM = `${transcriptId}(${maneLabel})`

          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              direction="column"
              alignItems="start"
            >
              <Stack
                direction="row"
                alignItems={'center'}
                justifyContent="space-between"
                width={'100%'}
              >
                {ability && ability.can('read', 'report-search') ? (
                  <Badges
                    onClick={(e) => {
                      e.stopPropagation()
                      props.setHistoryOpen(true)
                      props.setCurrent && props.setCurrent(row.original)
                    }}
                    badgeContent={hisBadges}
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      color: 'inherit',
                      '& .MuiBadge-badge': { top: '6px', right: '-45px' }
                    }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <Badges
                      onClick={(e) => {
                        e.stopPropagation()
                        props.setCommentsOpen(true)
                        props.setCurrent && props.setCurrent(row.original)
                      }}
                      showZero
                      badgeContent={badges}
                      color="primary"
                      sx={{
                        cursor: 'pointer',
                        color: 'inherit',
                        '& .MuiBadge-badge': { top: '6px', right: '-9px' }
                      }}
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <Tooltip title={getGeneName(row.original)}>
                        <TypographyA
                          sx={{
                            color:
                              bgcolor === 'rgba(229,38,35,0.8)'
                                ? 'white'
                                : [
                                    'rgb(107,148,200,0.8)',
                                    'rgb(109,56,149,0.8)'
                                  ].includes(bgcolor)
                                ? 'black'
                                : '#23527c',
                            fontWeight: '700',
                            cursor: 'pointer',
                            maxWidth: 80,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {getGeneName(row.original)}
                        </TypographyA>
                      </Tooltip>
                    </Badges>
                  </Badges>
                ) : (
                  <Badges
                    onClick={(e) => {
                      e.stopPropagation()
                      props.setCommentsOpen(true)
                      props.setCurrent && props.setCurrent(row.original)
                    }}
                    showZero
                    badgeContent={badges}
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      color: 'inherit',
                      '& .MuiBadge-badge': { top: '6px', right: '-9px' }
                    }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <Tooltip title={getGeneName(row.original)}>
                      <TypographyA
                        sx={{
                          color:
                            bgcolor === 'rgba(229,38,35,0.8)'
                              ? 'white'
                              : [
                                  'rgb(107,148,200,0.8)',
                                  'rgb(109,56,149,0.8)'
                                ].includes(bgcolor)
                              ? 'black'
                              : '#23527c',
                          fontWeight: '700',
                          cursor: 'pointer',
                          maxWidth: 80,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {getGeneName(row.original)}
                      </TypographyA>
                    </Tooltip>
                  </Badges>
                )}
              </Stack>

              <Stack
                sx={{ position: 'relative' }}
                maxWidth={'100%'}
                direction="row"
                alignItems="center"
              >
                <Tooltip
                  title={NM}
                  placement={'top-start'}
                  disableHoverListener={NM.length <= 14}
                >
                  <TypographyA
                    id="qw"
                    sx={{
                      width: '100%',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)'
                          ? 'white'
                          : [
                              'rgb(107,148,200,0.8)',
                              'rgb(109,56,149,0.8)'
                            ].includes(bgcolor)
                          ? 'black'
                          : '#23527c',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      window.open(
                        `${ExternalLinks.ncbi.nuccore}/${transcriptId}`
                      )
                    }}
                  >
                    {NM.length >= 14 ? NM : NM}
                  </TypographyA>
                </Tooltip>
              </Stack>
            </Stack>
          )
        },
        sorter: true,
        ...props.getColumnSearchProps('gene')
      },

      // 位置列
      {
        title: () => (
          <Stack maxWidth="100%">
            <Stack maxWidth="100%">
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Position')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('dbSNP')}
              </span>
            </Stack>
          </Stack>
        ),
        dataIndex: 'dbSNP',
        key: 'dbSNP',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const bgcolor = getBgColor(row.original)
          const rsId = getRsId(row.original)

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              <TypographyA
                sx={{
                  cursor: 'pointer',
                  color:
                    bgcolor === 'rgba(229,38,35,0.8)'
                      ? 'white'
                      : [
                          'rgb(107,148,200,0.8)',
                          'rgb(109,56,149,0.8)'
                        ].includes(bgcolor)
                      ? 'black'
                      : '#23527c',
                  fontWeight: '700'
                }}
                onClick={(e) => handleIGVClick(e, row.original)}
              >
                {`${row.original.chrom}:${row.original.pos}`}
              </TypographyA>

              <Tooltip
                disableHoverListener={!rsId || rsId.length <= 14}
                title={rsId}
                placement={'top-start'}
              >
                <TypographyA
                  sx={{
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)'
                        ? 'white'
                        : [
                            'rgb(107,148,200,0.8)',
                            'rgb(109,56,149,0.8)'
                          ].includes(bgcolor)
                        ? 'black'
                        : '#23527c',
                    cursor: 'pointer',
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    fontWeight: '700'
                  }}
                  onClick={() => {
                    window.open(
                      `${ExternalLinks.ncbi.snp_ref}?type=rs&rs=${rsId}`
                    )
                  }}
                >
                  {rsId || ''}
                </TypographyA>
              </Tooltip>
            </Stack>
          )
        },
        ...props.getColumnSearchProps('dbSNP')
      },

      // 功能列
      {
        title: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Function')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>{`${t(
                'Location'
              )}`}</span>
            </Stack>
          </Stack>
        ),
        dataIndex: 'gls_coding_effect',
        key: 'gls_coding_effect',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const consequence = getConsequence(row.original)
          const varLocation = getVarLocation(row.original)
          const exon = row.original.exon

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              <Tooltip
                disableHoverListener={!consequence || consequence.length <= 14}
                placement={'top-start'}
                title={consequence}
              >
                <TypographyA
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {consequence}
                </TypographyA>
              </Tooltip>

              <Tooltip
                disableHoverListener={
                  !varLocation ||
                  varLocation.length +
                    (exon && exon !== '.' ? exon.length + 2 : 0) <=
                    14
                }
                placement={'top-start'}
                title={`${varLocation}${
                  exon === '.' || !exon ? '' : `(${exon})`
                }`}
              >
                <TypographyA
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {`${varLocation}${exon === '.' || !exon ? '' : `(${exon})`}`}
                </TypographyA>
              </Tooltip>
            </Stack>
          )
        },
        ...props.getColumnSearchProps('tokenToLocations')
      },

      // 碱基变化列
      {
        title: () => (
          <React.Fragment>
            <Stack maxWidth="100%">
              <span style={{ textAlign: 'start' }}>{t('Base change')}</span>
            </Stack>
          </React.Fragment>
        ),
        dataIndex: 'nuc_change',
        key: 'nuc_change',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const flag = hasCNVFlag(row.original)
          const nucChange = getNucChange(row.original)
          const hgvsc = getHgvsc(row.original)
          const hgvsp = getHgvsp(row.original)

          return (
            <Stack
              maxWidth={'100%'}
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems={'center'}
            >
              <Box>
                <Stack
                  direction="row"
                  alignItems={'center'}
                  justifyContent="start"
                  width={'100%'}
                >
                  <Tooltip
                    disableHoverListener={!nucChange || nucChange.length <= 14}
                    title={nucChange}
                    placement="top-start"
                  >
                    <TypographyA
                      sx={{
                        maxWidth: 140,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        color: '#23527c',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                      onClick={(e) => {
                        props.setCurrent && props.setCurrent(row.original)
                        props.setFilterGeneOpen(true)
                        props.setMenuType('filter_gene')
                        props.setFilterGeneData([])
                        props.getFilterGene.mutateAsync(row.original.gene)
                        e.stopPropagation()
                        e.preventDefault()
                      }}
                    >
                      {nucChange}
                    </TypographyA>
                  </Tooltip>

                  {flag ? (
                    <TypographyA
                      sx={{ marginLeft: '15px', color: 'red !important' }}
                    >
                      cnv
                    </TypographyA>
                  ) : (
                    ''
                  )}
                </Stack>

                <Tooltip
                  disableHoverListener={!hgvsc || hgvsc.length <= 14}
                  title={hgvsc}
                  placement="top-start"
                >
                  <TypographyA
                    sx={{
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      textAlign: 'left'
                    }}
                  >
                    {hgvsc || ''}
                  </TypographyA>
                </Tooltip>

                <Tooltip
                  disableHoverListener={!hgvsp || hgvsp.length <= 14}
                  title={hgvsp}
                  placement="top-start"
                >
                  <TypographyA
                    sx={{
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      textAlign: 'left'
                    }}
                  >
                    {hgvsp || ''}
                  </TypographyA>
                </Tooltip>
              </Box>
            </Stack>
          )
        }
      },

      // Coverage列
      {
        title: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Tooltip
              placement="top-start"
              title={`实心圆表纯合，半实心表杂合；ref/alt；新发变异紫色加粗`}
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>{`${t(
                  'Zygosity'
                )}/${t('Coverage')}`}</span>

                {type === 'sample' ? (
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('QUAL')}
                  </span>
                ) : (
                  ''
                )}
              </Stack>
            </Tooltip>
          </Stack>
        ),
        dataIndex: 'ad',
        key: 'ad',
        align: 'center',
        width: 100,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const groupData = getGroupData(row.original)

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              {type === 'sample' ? (
                <React.Fragment>
                  {(row.original.allele_balance ||
                    row.original.allele_balance === 0) &&
                  row.original.allele_balance !== '.' ? (
                    <Stack direction={'row'} alignItems="center" spacing={1}>
                      <Stack maxWidth={'100%'} direction="column" spacing={1}>
                        {row.original.allele_balance < 0.85 ? (
                          <CircleHalfFull
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        ) : (
                          <Circle
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        )}
                      </Stack>

                      <TypographyA sx={{ maxWidth: '100%' }}>
                        {`${value}  [${row.original.allele_balance?.toFixed(
                          2
                        )}] `}
                      </TypographyA>
                    </Stack>
                  ) : (
                    ''
                  )}

                  <TypographyA sx={{ maxWidth: '100%', textAlign: 'left' }}>
                    {(row.original.qual as unknown as string) === '.' ||
                    !row.original.qual
                      ? '.'
                      : `${row.original.qual?.toFixed(1)}`}
                  </TypographyA>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {groupData.map((item: any) => {
                    if (item && item.allele_balance && item.ad) {
                      const temp = item
                      const mother = groupData.find(
                        (i: any) => i.type === 'mother'
                      )
                      const father = groupData.find(
                        (i: any) => i.type === 'father'
                      )

                      return (
                        <Tooltip
                          title={
                            temp
                              ? temp.type.includes('//')
                                ? `${t(temp.type.split('//')[0])}${
                                    temp.type.split('//')[1]
                                  }`
                                : t(temp.type)
                              : ''
                          }
                          placement="top-end"
                          key={item.sample_id}
                        >
                          <Stack
                            direction={'row'}
                            alignItems="center"
                            spacing={1}
                          >
                            <Stack
                              maxWidth={'100%'}
                              direction="column"
                              spacing={1}
                            >
                              {item.allele_balance < 0.85 ? (
                                <CircleHalfFull
                                  sx={{ color: 'black', fontSize: '0.875rem' }}
                                />
                              ) : (
                                <Circle
                                  sx={{ color: 'black', fontSize: '0.875rem' }}
                                />
                              )}
                            </Stack>

                            <TypographyA
                              sx={{
                                maxWidth: '100%',
                                color:
                                  !mother?.allele_balance &&
                                  !father?.allele_balance
                                    ? 'rgb(243,27,243)'
                                    : 'inherit',
                                fontWeight:
                                  !mother?.allele_balance &&
                                  !father?.allele_balance
                                    ? 'bold'
                                    : 'inherit'
                              }}
                            >
                              {`${item.ad} [${item.allele_balance.toFixed(
                                2
                              )}] (${
                                temp ? judgeType(temp.type, groupData) : ''
                              })`}
                            </TypographyA>
                          </Stack>
                        </Tooltip>
                      )
                    }
                  })}
                </React.Fragment>
              )}
            </Stack>
          )
        },
        ...props.getColumnSearchProps('coverage')
      },

      // Index列
      {
        title: () => (
          <Tooltip
            title={
              <>
                <p>用户下有该基因的样本量/用户有该基因的样本量</p>

                <p>组织下有该基因的样本量/组织有该基因的样本量</p>

                <p>平台下有该基因的样本量/平台有该基因的样本量</p>
              </>
            }
          >
            <Stack maxWidth={'100%'} direction={'column'} alignItems="center">
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Variant Index (User)')}
                </span>
              </Stack>

              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Variant Index (Organization)')}
                </span>
              </Stack>

              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Variant Index (GLS)')}
                </span>
              </Stack>
            </Stack>
          </Tooltip>
        ),
        dataIndex: 'index',
        key: 'index',
        align: 'center',
        width: 140,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const bgcolor = getBgColor(row.original)
          const tempGlsIndex = getIndexData(row.original)

          return (
            <Stack
              onClick={() => {
                props.setCurrent(row.original)
                props.setIndexOpen(true)
              }}
              maxWidth={'100%'}
              direction="column"
              spacing={1}
            >
              {tempGlsIndex ? (
                <React.Fragment>
                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                  >
                    {`${tempGlsIndex.user_index ?? 0} / ${
                      tempGlsIndex.user ?? 0
                    }`}
                  </TypographyA>

                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                  >
                    {`${tempGlsIndex.organization_index ?? 0} / ${
                      tempGlsIndex.organization ?? 0
                    }`}
                  </TypographyA>

                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                  >
                    {`${tempGlsIndex.platform_index ?? 0} / ${
                      tempGlsIndex.platform ?? 0
                    }`}
                  </TypographyA>
                </React.Fragment>
              ) : (
                ''
              )}
            </Stack>
          )
        },
        ...props.getColumnSearchProps('index')
      },

      // Articles列
      {
        title: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Articles')}
              </span>
            </Stack>
          </Stack>
        ),
        dataIndex: 'articles',
        key: 'articles',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const bgcolor = getBgColor(row.original)
          const LitVarArticles = row.original.LitVarArticles
          const Articles = row.original.Articles

          return (
            <Stack
              onClick={(e) => {
                e.stopPropagation()
                props.setAcmgOpen(true)
                props.setCurrent && props.setCurrent(row.original)
                setTimeout(() => {
                  props.VariationGeneRef.current?.handleClickLeft?.(
                    3,
                    'Publication'
                  )
                }, 2000)
              }}
              maxWidth={'100%'}
              direction="column"
              spacing={1}
              sx={{ cursor: 'pointer' }}
            >
              {LitVarArticles?.count ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                    textOverflow: 'ellipsis',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  LitVar:{LitVarArticles?.count ?? 0}
                </TypographyA>
              ) : (
                ''
              )}

              {Articles?.variantPubmedArticlesCount ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    textAlign: 'left',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  Clinvar:{Articles?.variantPubmedArticlesCount ?? 0}
                </TypographyA>
              ) : (
                ''
              )}

              {Articles?.genePubmedArticlesCount ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    textAlign: 'left',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  OMIM:{Articles?.genePubmedArticlesCount ?? 0}
                </TypographyA>
              ) : (
                ''
              )}
            </Stack>
          )
        }
      },

      // gnomAD列
      {
        title: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'} justifyContent="center">
              {props.query?.title === t('MT') ? (
                <>
                  <span
                    style={{ lineHeight: 'normal', textAlign: 'left' }}
                  >{`gAD_hom_all`}</span>

                  <span
                    style={{ lineHeight: 'normal', textAlign: 'left' }}
                  >{`gAD_het_all`}</span>
                </>
              ) : (
                <>
                  <span
                    style={{ lineHeight: 'normal', textAlign: 'left' }}
                  >{`GnomAD Exome ALL`}</span>

                  <span
                    style={{ lineHeight: 'normal', textAlign: 'left' }}
                  >{`GnomAD Genome ALL`}</span>

                  <span
                    style={{ lineHeight: 'normal', textAlign: 'left' }}
                  >{`${t('WBBC')}`}</span>
                </>
              )}
            </Stack>
          </Stack>
        ),
        dataIndex: 'gnomAD_exome_ALL',
        key: 'gnomAD_exome_ALL',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const gnomadData = getGnomadData(row.original)

          return (
            <Stack textAlign={'left'} maxWidth={'100%'} direction="column">
              {props.query?.title === t('MT') ? (
                <>
                  <TypographyA sx={{ maxWidth: '100%' }}>
                    {gnomadData.hom}
                  </TypographyA>

                  <TypographyA sx={{ maxWidth: '100%' }}>
                    {gnomadData.het}
                  </TypographyA>
                </>
              ) : (
                <>
                  <TypographyA sx={{ maxWidth: '100%' }}>
                    {gnomadData.exome}
                  </TypographyA>

                  <TypographyA sx={{ maxWidth: '100%' }}>
                    {gnomadData.genome}
                  </TypographyA>

                  <TypographyA sx={{ maxWidth: '100%' }}>
                    {gnomadData.wbbc}
                  </TypographyA>
                </>
              )}
            </Stack>
          )
        },
        ...props.getColumnSearchProps('gnomAD_exome_ALL')
      },

      // F Score列
      {
        title: () => (
          <Stack
            maxWidth={'100%'}
            direction={'row'}
            alignItems="center"
            justifyContent="center"
          >
            <Tooltip
              placement="top-start"
              title="F score:GLSofort 软件AI模型综合评分：致病性、质量、表型、遗传模式、文献等等，最可能的致病位点"
            >
              <Stack
                maxWidth={'100%'}
                justifyContent="center"
                alignContent={'center'}
              >
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('F')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  Score
                </span>
              </Stack>
            </Tooltip>
          </Stack>
        ),
        dataIndex: 'f_score',
        key: 'f_score',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const bgcolor = getBgColor(row.original)
          const groupData = getGroupData(row.original)

          return (
            <Stack maxWidth={'100%'}>
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                {type === 'sample' ? (
                  <TypographyA
                    onClick={(e) => {
                      e.stopPropagation()
                      props.setGeneOpen(true)
                      props.setCurrent && props.setCurrent(row.original)
                    }}
                    sx={{
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)'
                          ? 'white'
                          : [
                              'rgb(107,148,200,0.8)',
                              'rgb(109,56,149,0.8)'
                            ].includes(bgcolor)
                          ? 'black'
                          : '#23527c',
                      textAlign: 'center',
                      fontWeight: '700'
                    }}
                  >
                    {(row.original.f_score as any) !== '.'
                      ? (row.original.f_score * 100)?.toFixed(2)
                      : '.'}
                  </TypographyA>
                ) : (
                  groupData.map((item: any) => {
                    if (item && item.f_score) {
                      const temp = item
                      return (
                        <Tooltip
                          title={
                            temp
                              ? temp.type.includes('//')
                                ? `${t(temp.type.split('//')[0])}${
                                    temp.type.split('//')[1]
                                  }`
                                : t(temp.type)
                              : ''
                          }
                          placement="top-end"
                          key={item.sample_id}
                        >
                          <TypographyA
                            onClick={(e) => {
                              e.stopPropagation()
                              props.setGeneOpen(true)
                              props.setCurrent && props.setCurrent(row.original)
                            }}
                            sx={{
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              color:
                                bgcolor === 'rgba(229,38,35,0.8)'
                                  ? 'white'
                                  : [
                                      'rgb(107,148,200,0.8)',
                                      'rgb(109,56,149,0.8)'
                                    ].includes(bgcolor)
                                  ? 'black'
                                  : '#23527c',
                              textAlign: 'center',
                              fontWeight: '700'
                            }}
                          >
                            {(item.f_score as any) !== '.'
                              ? `${(item.f_score * 100)?.toFixed(2)}(${
                                  temp ? judgeType(temp.type, groupData) : ''
                                })`
                              : '.'}
                          </TypographyA>
                        </Tooltip>
                      )
                    }
                  })
                )}
              </Stack>
            </Stack>
          )
        },
        ...props.getColumnSearchProps('f_score')
      },

      // G Score列
      {
        title: () => (
          <Stack
            maxWidth={'100%'}
            direction={'row'}
            alignItems="center"
            justifyContent="center"
          >
            <Tooltip
              placement="top-start"
              title={t(
                'G score: Phenotypic correlation score, which is a comprehensive score associated with genetic diseases and other genetic diseases that interact with genes'
              )}
            >
              <Stack maxWidth={'100%'} justifyContent="center">
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('G')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  Score
                </span>
              </Stack>
            </Tooltip>
          </Stack>
        ),
        dataIndex: 'g_score',
        key: 'g_score',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const groupData = getGroupData(row.original)

          return (
            <Stack maxWidth={'100%'}>
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                {type === 'sample' ? (
                  <TypographyA
                    sx={{
                      fontSize: '0.8rem',
                      textAlign: 'center',
                      fontWeight: '700'
                    }}
                  >
                    {(row.original.g_score as any) !== '.'
                      ? (row.original.g_score * 100)?.toFixed(2)
                      : '.'}
                  </TypographyA>
                ) : (
                  groupData.map((item: any) => {
                    if (item && (item.g_score as any) !== '.') {
                      const temp = item
                      return (
                        <Tooltip
                          title={
                            temp
                              ? temp.type.includes('//')
                                ? `${t(temp.type.split('//')[0])}${
                                    temp.type.split('//')[1]
                                  }`
                                : t(temp.type)
                              : ''
                          }
                          placement="top-end"
                          key={item.sample_id}
                        >
                          <TypographyA
                            sx={{
                              fontSize: '0.8rem',
                              textAlign: 'center',
                              fontWeight: '700'
                            }}
                          >
                            {(item.g_score as any) !== '.'
                              ? `${(item.g_score * 100)?.toFixed(2)}(${
                                  temp ? judgeType(temp.type, groupData) : ''
                                })`
                              : '.'}
                          </TypographyA>
                        </Tooltip>
                      )
                    }
                  })
                )}
              </Stack>
            </Stack>
          )
        },
        ...props.getColumnSearchProps('g_score')
      },

      // SpliceAI/Revel/AlphaM列
      {
        title: () => (
          <Stack maxWidth={'100%'} alignItems="center">
            <Tooltip
              title={`D(${t('harmful')}) U(${t('unknown significance')}) B(${t(
                'benign'
              )})`}
              placement="top-start"
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('SpliceAI')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Revel')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('AlphaM')}
                </span>
              </Stack>
            </Tooltip>
          </Stack>
        ),
        dataIndex: 'spliceAI_max_score',
        key: 'spliceAI_max_score',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const spliceAI = getSpliceAI(row.original)
          const revel = getRevel(row.original)
          const alphaM = getAlphaM(row.original)

          return (
            <Stack maxWidth={'100%'}>
              <Stack
                maxWidth={'100%'}
                direction="column"
                spacing={1}
                justifyContent="center"
                alignItems="center"
              >
                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {spliceAI === ('.' as unknown as string)
                      ? '.'
                      : Number(spliceAI)?.toFixed(2)}
                  </span>

                  <span
                    style={{ fontWeight: spliceAI >= 0.2 ? 'bold' : 'normal' }}
                  >
                    {spliceAI >= 0.2 ? 'D' : spliceAI <= 0.1 ? '' : ''}
                  </span>
                </TypographyA>

                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {revel === ('.' as unknown as string)
                      ? '.'
                      : Number(revel)?.toFixed(3)}
                  </span>

                  <span
                    style={{ fontWeight: revel >= 0.644 ? 'bold' : 'normal' }}
                  >
                    {revel >= 0.644 ? 'D' : revel <= 0.29 ? '' : ''}
                  </span>
                </TypographyA>

                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {alphaM === ('.' as unknown as string) || !alphaM
                      ? '.'
                      : Number(alphaM)?.toFixed(2)}
                  </span>

                  <span
                    style={{
                      fontWeight: Number(alphaM) >= 0.564 ? 'bold' : 'normal'
                    }}
                  >
                    {Number(alphaM) >= 0.564
                      ? 'D'
                      : Number(alphaM) <= 0.34
                      ? ''
                      : ''}
                  </span>
                </TypographyA>
              </Stack>
            </Stack>
          )
        },
        ...props.getColumnSearchProps('SpliceAI_max_score')
      },

      // ClinVar列
      {
        title: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Clinvar (star)')}
              </span>
            </Stack>
          </Stack>
        ),
        dataIndex: 'CLINVAR_variant_id',
        key: 'CLINVAR_variant_id',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const bgcolor = getBgColor(row.original)
          const clinvarClassification = getClinvarClassification(row.original)
          const clinvarVariantId = getClinvarVariantId(row.original)
          const clinvarScore = getClinvarScore(row.original)

          return (
            <Stack maxWidth={'100%'} direction={'column'} spacing={1}>
              <Stack
                alignItems={'center'}
                maxWidth={'100%'}
                spacing={1}
                direction="row"
              >
                <Tooltip title={clinvarClassification} placement={'top-start'}>
                  <TypographyA
                    sx={{
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c',
                      cursor: 'pointer',
                      maxWidth: 80,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                    onClick={() => {
                      window.open(
                        `${ExternalLinks.ncbi.variation}/${clinvarVariantId}`
                      )
                    }}
                  >
                    {clinvarClassification || ''}
                  </TypographyA>
                </Tooltip>

                {clinvarScore && clinvarScore !== '.' ? (
                  <Rating
                    readOnly
                    value={clinvarScore as unknown as number}
                    max={clinvarScore as unknown as number}
                  />
                ) : (
                  ''
                )}
              </Stack>
            </Stack>
          )
        },
        filters: [
          { value: 'PAT', text: t('Pathogenic') },
          { value: 'LP', text: t('Likely Pathogenic') },
          { value: 'VUS', text: t('Uncertain Significance') },
          { value: 'LB', text: t('Likely Benign') },
          { value: 'BEN', text: t('Benign') },
          { value: 'CONF', text: t('Conf') },
          { value: '.', text: 'None' }
        ]
      },

      // ACMG列
      {
        title: () => (
          <React.Fragment>
            <Stack maxWidth={'100%'} direction={'column'} alignItems="center">
              <span>{t('ACMG')}</span>

              <span>{t('Classification')}</span>
            </Stack>
          </React.Fragment>
        ),
        dataIndex: 'ACMG_priority',
        key: 'ACMG_priority',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const acmg = getAcmgClassification(row.original)
            ? `${getAcmgClassification(row.original)}`
            : row.original.user_acmg_classification
            ? `${row.original.user_acmg_classification}`
            : 'VUS'
          const acmgScore = getAcmgScore(row.original)
          let bgcolor = color.find((i) => switchACMG(i.key) === acmg)
            ? (color.find((i) => switchACMG(i.key) === acmg)?.color as any)
            : 'none'

          if (row.original.user_acmg_classification) {
            bgcolor = color.find(
              (i) =>
                i.key === row.original.user_acmg_classification?.toLowerCase()
            )
              ? (color.find(
                  (i) =>
                    i.key ===
                    row.original.user_acmg_classification?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            return (
              <Stack maxWidth={'100%'}>
                <Box
                  sx={{
                    maxWidth: '100%',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Box>
                    <Chip
                      sx={{
                        bgcolor: bgcolor,
                        color:
                          bgcolor === 'rgb(240,239,240,0.4)'
                            ? 'black'
                            : 'white',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        minWidth: '45px',
                        textAlign: 'center'
                      }}
                      label={`${formatACMG(
                        row.original.user_acmg_classification
                      )}${acmgScore || acmgScore === 0 ? `:${acmgScore}` : ``}`}
                      size="small"
                      onClick={() => {
                        props.setAcmgOpen(true)
                        props.setCurrent && props.setCurrent(row.original)
                      }}
                    />
                  </Box>
                </Box>
              </Stack>
            )
          } else if (
            acmg !== '.' ||
            row.original.user_acmg_classification !== '.'
          ) {
            return (
              <Stack maxWidth={'100%'}>
                <Box
                  sx={{
                    maxWidth: '100%',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Box>
                    <Chip
                      sx={{
                        bgcolor: bgcolor,
                        color:
                          bgcolor === 'rgb(240,239,240,0.4)'
                            ? 'black'
                            : 'white',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        minWidth: '45px',
                        fontWeight: '700',
                        textAlign: 'center'
                      }}
                      label={`${formatACMG(acmg)}${
                        acmgScore || acmgScore === 0 ? `:${acmgScore}` : ``
                      }`}
                      size="small"
                      onClick={() => {
                        props.setAcmgOpen(true)
                        props.setCurrent && props.setCurrent(row.original)
                      }}
                    />
                  </Box>

                  <Box>
                    {row.original.user_classification ? (
                      <Chip
                        key={row.original.user_classification}
                        size="small"
                        label={formatACMG(row.original.user_classification)}
                        sx={{
                          bgcolor: bgcolor,
                          color:
                            bgcolor === 'rgb(240,239,240,0.4)'
                              ? 'black'
                              : 'white',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          textAlign: 'center',
                          minWidth: '45px'
                        }}
                        onClick={() => {
                          props.setCurrent(row.original)
                          props.setOpen(true)
                        }}
                      />
                    ) : (
                      <IconButton
                        aria-label="edit"
                        onClick={() => {
                          props.setCurrent(row.original)
                          props.setOpen(true)
                        }}
                        sx={{ padding: 0 }}
                      >
                        <CommentOutline sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Stack>
            )
          } else {
            return (
              <>
                <TypographyA
                  sx={{ maxWidth: '100%', textAlign: 'center' }}
                >{`${acmgScore}`}</TypographyA>

                <TypographyA textAlign={'center'}>.</TypographyA>
              </>
            )
          }
        },
        sorter: true,
        ...props.getColumnSearchProps('ACMG_priority')
      },

      // OMIM列
      {
        title: () => (
          <Stack width={'100%'} direction={'row'} justifyContent="center">
            <Stack maxWidth={'100%'} justifyContent="center">
              {props.query && props.query.key && props.query.key === '5' ? (
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Disease Information')}
                </span>
              ) : (
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('OMIM')}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Inheritance')}
                  </span>
                </React.Fragment>
              )}
            </Stack>
          </Stack>
        ),
        dataIndex: 'inheritance',
        key: 'inheritance',
        align: 'center',
        width: 80,
        ellipsis: true,
        render: (value: any, rows: any) => {
          const row = { original: rows }
          const { data: OMIMValue, type: omimType } = getOMIMData(row.original)

          return (
            <Stack maxWidth={'100%'} spacing={1}>
              {OMIMValue && OMIMValue.length ? (
                <HtmlTooltip
                  title={renderTooltipContent(OMIMValue, omimType)}
                  placement={'top-start'}
                  TransitionComponent={Slide}
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      maxWidth: isWindow() ? '400px' : '200px'
                    }
                  }}
                >
                  <Box>
                    {OMIMValue.slice(0, 2).map((items: any, index: any) =>
                      renderOMIMItem(items, index, omimType, row.original)
                    )}
                  </Box>
                </HtmlTooltip>
              ) : (
                <TypographyA>.</TypographyA>
              )}
            </Stack>
          )
        },
        sorter: true,
        ...props.getColumnSearchProps('inheritance')
      }
    ]
    if (!props.indexChecked) {
      column = column.filter((i) => i.id !== 'index')
    }

    return column
  }, [props, t, i18n, ability, isPipelineVersion])

  React.useImperativeHandle(ref, () => ({
    column
  }))

  return <></>
})

export default Columns2
